import { DevicesResponse, PartitionAdminResponse } from '../../../NonReact/Types/Types'
import { getDevices, getPartitions, getTelSysInternal } from '../../../NonReact/common/API/EntityAPIClient'
import { ALL_VALUE } from '../Constants/DefaultValues'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import {
    MAX_PARTITIONS_TO_CHECK,
    PARTITION_DESCRIPTIONS_COMMON_DEVICES_PARTITION
} from '../../../NonReact/constants/PartitionConstants'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ErrorLanguage } from '../../../../i18n'
import { displayErrorMessage } from '../Utils/Messages'

/**
 * Get all devices from all given partitions.
 *
 * @param telSysID - The ID of the TelSys.
 * @param partitionIDs - The IDs of the partitions to get devices from.
 * @param monitoredOnly - Whether to only get monitored devices.
 * @param deviceTypeFilter - The device types to filter by.
 * @param searchQuery - The search query to filter by.
 * @param cacheTime - The time to cache the response.
 * @param deviceFields - The fields to get from the devices.
 *
 * @returns A promise that resolves to the devices.
 */
const getDevicesFromPartitions = async (
    telSysID: string | number,
    partitionIDs: (string | number)[],
    monitoredOnly: boolean,
    deviceTypeFilter?: string[],
    searchQuery?: string,
    cacheTime?: number,
    deviceFields?: string[]
): Promise<DevicesResponse[]> =>
    Promise.all(
        partitionIDs.map(
            (partitionID: string | number): Promise<DevicesResponse[]> =>
                getDevices(telSysID, partitionID, monitoredOnly, deviceTypeFilter, searchQuery, cacheTime, deviceFields)
        )
    ).then((devices: DevicesResponse[][]): DevicesResponse[] => devices.flat())

/**
 * Get all devices from all partitions in a TelSys.
 *
 * @param telSysID - The ID of the TelSys.
 * @param partitionIDs - The IDs of the partitions to get devices from.
 * @param monitoredOnly - Whether to only get monitored devices.
 * @param deviceTypeFilter - The device types to filter by.
 * @param searchQuery - The search query to filter by.
 * @param cacheTime - The time to cache the response.
 * @param deviceFields - The fields to get from the devices.
 *
 * @returns A promise that resolves to the devices.
 */
export const getDevicesFromTelSys = async (
    telSysID: string | number,
    partitionIDs?: (string | number)[],
    monitoredOnly?: boolean,
    deviceTypeFilter?: string[],
    searchQuery?: string,
    cacheTime?: number,
    deviceFields?: string[]
): Promise<DevicesResponse[]> => {
    if (partitionIDs && partitionIDs.length > 0 && partitionIDs[0] !== ALL_VALUE) {
        return getDevicesFromPartitions(
            telSysID,
            partitionIDs,
            monitoredOnly ?? false,
            deviceTypeFilter,
            searchQuery,
            cacheTime,
            deviceFields
        )
    }

    // Get Telephony Server.
    const allowAllPartitionLookup: boolean =
        (await getTelSysInternal(telSysID))?.Advanced?.PartitionsRptingScopeAllEnabled ?? false

    // Get all partitions.
    const allPartitionIDs: number[] = await getPartitions(telSysID).then(
        (partitions: PartitionAdminResponse[]): number[] =>
            partitions
                .filter(
                    (partition: PartitionAdminResponse): boolean =>
                        isVarNotEmpty(partition.PartIDInTelSys) &&
                        partition.Description !== PARTITION_DESCRIPTIONS_COMMON_DEVICES_PARTITION
                )
                .map((partition: PartitionAdminResponse): number => partition.PartIDInTelSys ?? 0)
    )

    // If the Telephony Server doesn't allow all partition lookup, reduce the array to first 5 entries.
    if (!allowAllPartitionLookup && allPartitionIDs.length > MAX_PARTITIONS_TO_CHECK) {
        const language: CLanguageHandler = CLanguageHandler.getInstance()
        language.loadBundle(ErrorLanguage.NAME)

        displayErrorMessage(language.getInterpolated(ErrorLanguage.MaxPartitions, MAX_PARTITIONS_TO_CHECK))

        allPartitionIDs.splice(MAX_PARTITIONS_TO_CHECK)
    }

    // Get all devices from all partitions.
    return getDevicesFromPartitions(
        telSysID,
        allPartitionIDs,
        monitoredOnly ?? false,
        deviceTypeFilter,
        searchQuery,
        cacheTime,
        deviceFields
    )
}
